import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { statusLabels } from "../../lib/constants";

// ==================== TRAININGS PAGE ====================
const TrainingsPage = () => {
  const [trainings, setTrainings] = useState([]);
  const [myTrainings, setMyTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [allResp, myResp] = await Promise.all([
          api.get('/portal/trainings'),
          api.get('/portal/trainings/my')
        ]);
        setTrainings(allResp.data);
        setMyTrainings(myResp.data);
      } catch (error) {
        console.error('Error fetching trainings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleEnroll = async (trainingId) => {
    try {
      await api.post(`/portal/trainings/${trainingId}/enroll`);
      toast.success('Inscrição realizada com sucesso');
      const myResp = await api.get('/portal/trainings/my');
      setMyTrainings(myResp.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao inscrever');
    }
  };

  const handleExportICS = async (trainingId) => {
    try {
      const response = await api.get(`/portal/trainings/${trainingId}/export-ics`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `formacao_${trainingId}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Ficheiro ICS descarregado - importe para o seu calendário');
    } catch (error) {
      toast.error('Erro ao exportar calendário');
    }
  };

  return (
    <div data-testid="trainings-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Formações</h1>
        <p className="text-muted-foreground">Inscreva-se em formações e submeta evidências de participação</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button onClick={() => setActiveTab('available')} className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'available' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>Disponíveis</button>
        <button onClick={() => setActiveTab('my')} className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'my' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>Minhas Formações</button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
      ) : activeTab === 'available' ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trainings.filter(t => t.status === 'agendada').map((t) => (
            <div key={t.training_id} className="rounded-xl border bg-card p-5 hover:border-primary/50 transition-all">
              <div className="flex items-start justify-between mb-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${t.category === 'obrigatoria' ? 'bg-red-100 text-red-700' : t.category === 'compliance' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {t.category === 'obrigatoria' ? 'Obrigatória' : t.category === 'compliance' ? 'Compliance' : 'Opcional'}
                </span>
                <span className="text-sm text-muted-foreground">{t.duration_hours}h</span>
              </div>
              <h3 className="font-semibold text-lg mb-2">{t.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{t.description?.substring(0, 100)}</p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  {t.date}
                </span>
                {t.location && <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                  {t.location}
                </span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEnroll(t.training_id)} className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90">Inscrever</button>
                <button onClick={() => handleExportICS(t.training_id)} className="px-3 py-2 rounded-lg border hover:bg-muted" title="Adicionar ao calendário">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </button>
              </div>
            </div>
          ))}
          {trainings.filter(t => t.status === 'agendada').length === 0 && (
            <div className="col-span-full text-center py-12 bg-card rounded-xl border"><p className="text-muted-foreground">Sem formações disponíveis</p></div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {myTrainings.map((t) => (
            <div key={t.training_id} className="rounded-xl border bg-card p-5 flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{t.name}</h3>
                <p className="text-sm text-muted-foreground">{t.date} • {t.duration_hours}h</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${t.my_status === 'concluido' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                  {statusLabels[t.my_status] || t.my_status}
                </span>
                <button onClick={() => handleExportICS(t.training_id)} className="p-2 rounded-lg border hover:bg-muted" title="Adicionar ao calendário">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </button>
              </div>
            </div>
          ))}
          {myTrainings.length === 0 && (
            <div className="text-center py-12 bg-card rounded-xl border"><p className="text-muted-foreground">Sem inscrições em formações</p></div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrainingsPage;
